import { useEffect, useRef } from 'react';
import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { registerDevice, type DevicePlatform } from '@monecolier/api-client';
import { useAuth } from '@monecolier/ui';
import { apiClient } from '../services/api';

function resolveDevicePlatform(): DevicePlatform | null {
  const platform = Capacitor.getPlatform();
  if (platform === 'ios') return 'IOS';
  if (platform === 'android') return 'ANDROID';
  // Web (dev local) ou toute autre plateforme non gérée par MOB-06 — jamais
  // une erreur, simplement rien à enregistrer.
  return null;
}

/**
 * MOB-06 — enregistre l'appareil auprès du backend (`POST /devices`) dès
 * qu'un compte est authentifié, UNIQUEMENT dans la coquille NATIVE
 * (`Capacitor.isNativePlatform()`) — jamais dans un navigateur web classique
 * (dev local `pnpm dev`, ou même le build web déployé), où
 * `@capacitor/push-notifications` n'a structurellement aucun sens.
 *
 * Dupliqué À L'IDENTIQUE dans `apps/web-admin` ET `apps/web-parent` — jamais
 * un package partagé (`packages/ui` n'a aucune dépendance Capacitor) :
 * Capacitor est un souci NATIF PROPRE À CHAQUE APP (appId/capacitor.config.ts
 * distincts, MOB-03/MOB-04), même précédent déjà établi par
 * `apps/worker/src/lib/{mailer,storage,userName}.ts` (duplication délibérée
 * plutôt qu'un package partagé forcé où il ne s'impose pas structurellement).
 *
 * Objectif 3 du ticket (refus de permission) : géré EN SILENCE — un refus
 * (`denied`) ou une erreur native quelconque n'appelle jamais
 * `registerDevice()`, ne relance JAMAIS d'exception, et n'affecte rien
 * d'autre dans l'app (l'utilisateur continue simplement sans notifications
 * push).
 */
export function usePushNotificationRegistration(): void {
  const { accessToken, isAuthenticated } = useAuth();
  // Lu par le listener `registration` AU MOMENT de l'appel (jamais dans le
  // tableau de dépendances de l'effet ci-dessous) — sinon un simple
  // rafraîchissement silencieux d'access token (AUT-30, sur 401) relancerait
  // toute la danse permission/register native à chaque fois, sans aucun
  // bénéfice (le jeton device, LUI, ne change pas).
  const accessTokenRef = useRef(accessToken);
  accessTokenRef.current = accessToken;
  const registeredTokenRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !Capacitor.isNativePlatform()) {
      registeredTokenRef.current = null;
      return;
    }

    const resolvedPlatform = resolveDevicePlatform();
    if (!resolvedPlatform) {
      return;
    }
    // Ré-affectation en `const` typée strictement `DevicePlatform` (jamais
    // `| null`) — évite de compter sur la narrowing TypeScript à travers la
    // fermeture asynchrone de `setup()` ci-dessous, qui capture cette
    // variable plusieurs niveaux plus loin.
    const platform: DevicePlatform = resolvedPlatform;

    let cancelled = false;
    const listenerHandles: Array<Promise<PluginListenerHandle>> = [];

    async function setup() {
      try {
        const current = await PushNotifications.checkPermissions();
        let status = current.receive;
        if (status === 'prompt' || status === 'prompt-with-rationale') {
          const requested = await PushNotifications.requestPermissions();
          status = requested.receive;
        }
        if (cancelled) {
          return;
        }
        if (status !== 'granted') {
          // Refus explicite (ou tout autre statut non accordé) — jamais
          // bloquant, aucun enregistrement tenté.
          return;
        }

        listenerHandles.push(
          PushNotifications.addListener('registration', (token) => {
            if (cancelled) return;
            const currentAccessToken = accessTokenRef.current;
            if (!currentAccessToken) return;
            if (registeredTokenRef.current === token.value) return;
            registeredTokenRef.current = token.value;
            registerDevice(apiClient, { token: token.value, platform }, currentAccessToken).catch((error: unknown) => {
              console.error("[push] échec de l'enregistrement du jeton device", error);
            });
          }),
        );
        listenerHandles.push(
          PushNotifications.addListener('registrationError', (error) => {
            console.error("[push] échec natif de l'enregistrement push", error);
          }),
        );

        await PushNotifications.register();
      } catch (error) {
        // Toute autre erreur native (plugin indisponible hors d'une vraie
        // build Capacitor, etc.) — jamais propagée hors de ce hook.
        console.error("[push] initialisation des notifications push impossible", error);
      }
    }

    void setup();

    return () => {
      cancelled = true;
      for (const handlePromise of listenerHandles) {
        handlePromise.then((handle) => handle.remove()).catch(() => {});
      }
    };
  }, [isAuthenticated]);
}
